import {Injectable, isDevMode} from "@angular/core";
import {DateAdapter, MatDateFormats} from "@angular/material/core";
import {Intl, Temporal} from "temporal-polyfill";
import {erTomEllerNil} from "felles/stoettefunk/strenger";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {GjeldendeLokale, Lokaleinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {lesPlainDateFraStreng} from "felles-ng2/støttefunksjoner/temporal";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('PlainDateDateAdapter');

export const MAT_PLAINDATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'mm.dd', //brukes som format-argument til parse(..), benyttes ikke - vi utleder format fra locale, se localeSensitivtParseFormat()
    },
    display: {
        dateInput: {year: 'numeric', month: 'numeric', day: 'numeric'},
        monthYearLabel: {year: 'numeric', month: 'short'},
        dateA11yLabel: {year: 'numeric', month: 'long', day: 'numeric'},
        monthYearA11yLabel: {year: 'numeric', month: 'long'},
    },
};

/**
 * Implementasjon av Angular Materials DateAdapter for håndtering av js Temporal PlainDate.
 * Se https://tc39.es/proposal-temporal/docs/plaindate.html
 * og https://material.angular.io/components/datepicker/overview#customizing-the-parse-and-display-formats
 * @todo skille mellom en-US og en-GB
 */
@Injectable()
export class PlainDateDateAdapter extends DateAdapter<PlainDate, Lokaleinformasjon> {

    static ugyldig_dato = new PlainDate(0,1,1);

    constructor(gjeldendeLokale:GjeldendeLokale) {
        super();
        if (erNil(gjeldendeLokale)){
            if(isDevMode()) throw new Error(`PlainDateDateAdapter: gjeldendeLokale er null`);
            else super.setLocale(Lokaleinformasjon.standardLokale);
        } else {
            gjeldendeLokale.brukersLokaleSomStrøm().subscribe(lokale => {
                debug(`setter lokale til ${lokale.somLocaleId()}`);
                super.setLocale(lokale)
            })
        }
    }

    override getYear(date: PlainDate): number {
        return date.year;
    }

    override getMonth(date: PlainDate): number {
        return date.month - 1;
    }

    override getDate(date: PlainDate): number {
        return date.day;
    }

    override getDayOfWeek(date: PlainDate): number {
        //date.dayOfWeek ->  Monday being 1, and Sunday 7
        return date.dayOfWeek % 7;
    }

    override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
        return range(12, i => new PlainDate(2017, i + 1, 1).toLocaleString(this.locale.somLocaleId(), {month: style}));
    }

    override getDateNames(): string[] {
        //localen 'nb' gir tall etterfulgt av '.', feks '31.'
        //return range(31, i => new PlainDate(2017, 1, i + 1).toLocaleString(this.locale, {day: 'numeric'}));
        return range(31, i => '' + (i + 1));
    }

    override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
        return range(7, i => new PlainDate(2017, 1, i + 1).toLocaleString(this.locale.somLocaleId(), {weekday: style}));
    }

    override getYearName(date: PlainDate): string {
        return date.toLocaleString(this.locale.somLocaleId(), {year: 'numeric'})
    }

    override getFirstDayOfWeek(): number {
        //@todo skille mellom 'en-US' og 'en-GB' - som har ulike "første dag i uken"
        //new Intl.Locale('en-US').getWeekInfo().firstDay gir svaret - men finner ingen måte få tak i et Locale objekt fra Intl
        return 1; //mandag er første ukedag, i alle fall i europa
    }

    override getNumDaysInMonth(date: PlainDate): number {
        return date.daysInMonth
    }

    override clone(date: PlainDate): PlainDate {
        return PlainDate.from(date);
    }

    override createDate(year: number, month: number, date: number): PlainDate {
        //new PlainDate forventer 1-basert måned, DateAdapter forventer 0-basert måned
        return new PlainDate(year, month + 1, date);
    }

    override today(): PlainDate {
        return Temporal.Now.plainDateISO();
    }

    /** Konverterer fra streng bruker har skrevet inn i tekst-feltet til PlainDate.
     * Fra https://tc39.es/proposal-temporal/docs/plaindate.html#from:
     * Any non-object value is converted to a string, which is expected to be in ISO 8601 format.
     *
     * Fra https://tc39.es/proposal-temporal/docs/strings.html#why-doesnt-temporal-support-parsing-localized-formats-like-mmddyy:
     * Because of the complexity involved in standardizing and implementing localized date parsing, TC39 (the standards committee responsible for ECMAScript)
     * has learned from the painful experience of Date.parse that localized parsing is best left to userland libraries,
     * not platform specifications.*/
    override parse(verdi: any, parseFormat?: any): PlainDate | null {
        debug('parse', verdi, parseFormat)
        if(erTomEllerNil(verdi)) return null;
        switch (typeof verdi){
            case 'number': return this.parseTall(verdi);
            case 'string': return this.parseStreng(verdi);
            default:
                debug(`parse: angitt verdi '${verdi}' er av ikke-håndtert type ${typeof verdi}`)
                return null;
        }
    }

    override format(date: PlainDate, displayFormat: Intl.DateTimeFormatOptions): string {
        if (!this.isValid(date))
            throw Error('PlainDateDateAdapter: kan ikke formatere ugyldig PlainDate.');
        return date.toLocaleString(this.locale.somLocaleId(), {...displayFormat, timeZone: 'utc'} as Intl.DateTimeFormatOptions) //@todo hvorfor utc param?
    }

    override addCalendarYears(date: PlainDate, years: number): PlainDate {
        return this.addCalendarMonths(date, years * 12);
    }

    override addCalendarMonths(date: PlainDate, months: number): PlainDate {
        return date.add({months: months});
    }

    override addCalendarDays(date: PlainDate, days: number): PlainDate {
        return date.add({days: days});
    }

    override toIso8601(date: PlainDate): string {
        return date.toString();
    }

    override deserialize(verdi: any): PlainDate | null {
        if (erNil(verdi)) return null;
        if (verdi instanceof PlainDate) return verdi;
        if (typeof verdi === 'string') return PlainDate.from(verdi); //forventer at value er en ISO 8601

        return super.deserialize(verdi);
    }

    override isDateInstance(obj: any) {
        return obj instanceof PlainDate;
    }

    override isValid(date: PlainDate) {
        if (date === this.invalid()) return false;
        if (date.year < 1000) return false; //for å signalisere at årstall må skrives med 4 tegn (yyyy)
        return true;
    }

    override invalid(): PlainDate {
        return PlainDateDateAdapter.ugyldig_dato;
    }

    /** Returnerer locale sensitiv formatstreng som angir formatet som kan brukes i tekstfelt for angivelse av dato.
     * Se {@link Lokaleinformasjon#datoformat} */
    localeSensitivtParseFormat() {
        return this.locale.datoformat();
    }

    //hjelpemetoder

    private parseTall(verdi:number){
        return Temporal.Instant.fromEpochMilliseconds(verdi)
          .toZonedDateTimeISO('utc')
          .toPlainDate()
    }

    /** Konverterer fra streng til PlainDate vhja date-fns.parse metode.
     * Se https://date-fns.org/v2.16.1/docs/parse */
    private parseStreng(verdi:string){
        const plainDate = lesPlainDateFraStreng(verdi, this.locale);
        if (erNil(plainDate)) return this.invalid();
        return plainDate;
    }
}

function range<T>(length: number, valueFunction: (index: number) => T): T[] {
    const valuesArray = Array(length);
    for (let i = 0; i < length; i++) {
        valuesArray[i] = valueFunction(i);
    }
    return valuesArray;
}
